import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "./adminDashboard.css";
import Header from "../../Component/Header/Header";
import Sidebar from "../../Component/Sidebar/Sidebar";

const REACT_APP_EXPRESS_API = process.env.REACT_APP_EXPRESS_API;

function AdminDashboard() {
  const getIsMobile = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 1024px)").matches;

  const [isMobile, setIsMobile] = useState(getIsMobile());
  const [isSidebarOpen, setIsSidebarOpen] = useState(!getIsMobile());

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1024px)");
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    setIsSidebarOpen(!isMobile);
  }, [isMobile]);

  const applyDomClasses = useCallback(
    (open) => {
      const body = document.body;
      const sidebarEl = document.querySelector(".admin-page .sidebar");
      const overlayEl = document.querySelector(".admin-page .sidebar-overlay");

      if (isMobile) {
        if (open) {
          sidebarEl?.classList.add("is-mobile-open");
          overlayEl?.classList.add("is-visible");
          body.classList.add("lock-scroll");
        } else {
          sidebarEl?.classList.remove("is-mobile-open");
          overlayEl?.classList.remove("is-visible");
          body.classList.remove("lock-scroll");
        }
      } else {
        sidebarEl?.classList.remove("is-mobile-open");
        overlayEl?.classList.remove("is-visible");
        body.classList.remove("lock-scroll");
      }
    },
    [isMobile]
  );

  useEffect(() => {
    applyDomClasses(isSidebarOpen);
  }, [isSidebarOpen, applyDomClasses]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const handleOverlayClick = () => setIsSidebarOpen(false);

  const fetchUsers = useCallback(async (currentPage, currentLimit) => {
    setLoading(true);
    try {
      const res = await axios.get(`${REACT_APP_EXPRESS_API}/api/v2/users?page=${currentPage}&limit=${currentLimit}`, {
        withCredentials: true,
      });
      // Handle both the paginated API response and potential older API responses just in case
      setUsers(res.data.data || res.data);
      if (res.data.totalPages) {
        setTotalPages(res.data.totalPages);
      }
    } catch (err) {
      console.error("Failed to fetch users", err);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Gagal mengambil data user',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(page, limit);
  }, [fetchUsers, page, limit]);

  const handleActivate = async (userId) => {
    try {
      await axios.put(`${REACT_APP_EXPRESS_API}/api/v2/users/${userId}/activated`, {}, {
        withCredentials: true,
      });
      Swal.fire('Berhasil!', 'User berhasil diaktifkan.', 'success');
      fetchUsers(page, limit);
    } catch (err) {
      console.error(err);
      Swal.fire('Gagal', err.response?.data?.message || 'Terjadi kesalahan', 'error');
    }
  };

  const handleDeactivate = async (userId) => {
    const result = await Swal.fire({
      title: 'Nonaktifkan User?',
      text: "User tidak akan bisa login!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f39c12',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ya, Nonaktifkan!'
    });

    if (result.isConfirmed) {
      try {
        await axios.put(`${REACT_APP_EXPRESS_API}/api/v2/users/${userId}/deactivated`, {}, {
          withCredentials: true,
        });
        Swal.fire('Dinonaktifkan!', 'User telah dinonaktifkan.', 'success');
        fetchUsers(page, limit);
      } catch (err) {
        console.error(err);
        Swal.fire('Gagal', err.response?.data?.message || 'Terjadi kesalahan', 'error');
      }
    }
  };

  const handleDelete = async (userId) => {
    const result = await Swal.fire({
      title: 'Hapus User secara permanen?',
      text: "Data yang dihapus tidak bisa dikembalikan!",
      icon: 'error',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ya, Hapus!'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${REACT_APP_EXPRESS_API}/api/v2/users/${userId}`, {
          withCredentials: true,
        });
        Swal.fire('Terhapus!', 'User berhasil dihapus.', 'success');
        fetchUsers(page, limit);
      } catch (err) {
        console.error(err);
        Swal.fire('Gagal', err.response?.data?.message || 'Gagal menghapus user', 'error');
      }
    }
  };

  return (
    <div className={`admin-page ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>
      <div className="sidebar-overlay" onClick={handleOverlayClick}></div>
      <Sidebar isOpen={isSidebarOpen} onClose={handleOverlayClick} />

      <div className="main-content">
        <Header toggleSidebar={toggleSidebar} />

        <div className="admin-dashboard-container">
          <h2>Admin Dashboard - Manajemen User</h2>

          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nama</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Tanggal Buat</th>
                  <th>Tanggal Expired</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center">Loading...</td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">Tidak ada user ditemukan</td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user._id}>
                      <td>{user.nama}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>{new Date(user.createdAt).toLocaleDateString('id-ID')}</td>
                      <td>  {user.subscription_expires_at ? new Date(user.subscription_expires_at).toLocaleDateString('id-ID') : '-'}</td>
                      <td>
                        <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                          {user.isActive ? 'Aktif' : 'Non-Aktif'}
                        </span>
                      </td>
                      <td className="action-buttons">
                        {!user.isActive ? (
                          <button
                            className="btn-action activate"
                            onClick={() => handleActivate(user._id)}
                          >
                            Aktifkan
                          </button>
                        ) : (
                          <button
                            className="btn-action deactivate"
                            onClick={() => handleDeactivate(user._id)}
                            disabled={user.role === 'admin'}
                          >
                            Nonaktifkan
                          </button>
                        )}
                        <button
                          className="btn-action delete"
                          onClick={() => handleDelete(user._id)}
                          disabled={user.role === 'admin' || user.isActive}
                        >
                          Hapus
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div className="limit-selector">
              <label htmlFor="limit" style={{ marginRight: '10px', fontSize: '14px', color: '#555' }}>Tampilkan:</label>
              <select 
                id="limit"
                value={limit} 
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', outline: 'none', cursor: 'pointer' }}
              >
                <option value={5}>5 Data</option>
                <option value={10}>10 Data</option>
                <option value={20}>20 Data</option>
                <option value={50}>50 Data</option>
              </select>
            </div>
            
            <div className="pagination-controls" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))} 
                disabled={page === 1}
                style={{ 
                  padding: '8px 16px', 
                  cursor: page === 1 ? 'not-allowed' : 'pointer', 
                  borderRadius: '6px', 
                  border: '1px solid #e0e0e0', 
                  backgroundColor: page === 1 ? '#f9f9f9' : '#fff',
                  color: page === 1 ? '#a0a0a0' : '#333',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                Sebelumnya
              </button>
              <span style={{ fontSize: '14px', color: '#555', fontWeight: '500' }}>
                Halaman {page} dari {totalPages || 1}
              </span>
              <button 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                disabled={page === totalPages || totalPages === 0}
                style={{ 
                  padding: '8px 16px', 
                  cursor: (page === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer', 
                  borderRadius: '6px', 
                  border: '1px solid #e0e0e0', 
                  backgroundColor: (page === totalPages || totalPages === 0) ? '#f9f9f9' : '#fff',
                  color: (page === totalPages || totalPages === 0) ? '#a0a0a0' : '#333',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                Selanjutnya
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
